import { collection, query, where } from "firebase/firestore";
import { useMemo } from "react";
import { db } from "@/firebase/app";
import { useCollection } from "@/hooks/firestoreHooks";
import type { LeagueCompetitionEventDisplay } from "@/models/eventDisplay";
import { parseMemberSummary, type LeagueMemberSummary } from "@/models/memberSummary";
import { parseOrganization, type OrganizationModel } from "@/models/organization";
import { useActiveLeagueScope, useLeagueCompetitions } from "@/features/competitions/useLeagueCompetitions";

// Realtime league home snapshot — the web equivalent of LeagueHomeService,
// driven by onSnapshot listeners instead of one-shot fetches.

export { useActiveLeagueScope as useActiveLeagueID } from "@/features/competitions/useLeagueCompetitions";

export interface LeagueSnapshot {
  leagueID: string | null;
  organizationID: string | null;
  competitions: LeagueCompetitionEventDisplay[];
  organizations: OrganizationModel[];
  memberSummaries: LeagueMemberSummary[];
  loading: boolean;
  error: string | null;
}

export function useLeagueSnapshot(scopeToOrganization = false): LeagueSnapshot {
  const { leagueID, organizationID } = useActiveLeagueScope();
  const competitionSnapshot = useLeagueCompetitions();

  const organizationsQuery = leagueID ? query(collection(db, "organizations"), where("leagueID", "==", leagueID)) : null;
  const membersQuery = leagueID ? query(collection(db, "leagueMemberSummaries"), where("leagueID", "==", leagueID)) : null;

  const organizations = useCollection(organizationsQuery, parseOrganization, `orgs:${leagueID}`);
  const members = useCollection(membersQuery, parseMemberSummary, `members:${leagueID}`);

  return useMemo(() => {
    let scopedOrganizations = organizations.data;
    let scopedMembers = members.data;
    if (scopeToOrganization && organizationID) {
      scopedOrganizations = organizations.data.filter((org) => org.id === organizationID);
      scopedMembers = members.data.filter((member) => member.organizationIDs.includes(organizationID));
    }
    return {
      leagueID,
      organizationID,
      competitions: competitionSnapshot.competitions,
      organizations: [...scopedOrganizations].sort((a, b) => a.name.localeCompare(b.name)),
      memberSummaries: [...scopedMembers].sort((a, b) => a.displayName.localeCompare(b.displayName)),
      loading: competitionSnapshot.loading || organizations.loading || members.loading,
      error: competitionSnapshot.error ?? organizations.error ?? members.error,
    };
  }, [competitionSnapshot, organizations, members, leagueID, organizationID, scopeToOrganization]);
}
